import React from 'react';
import { Shield, CheckCircle, Clock, DollarSign, ClipboardCheck, Calculator, Banknote } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
import { MultiStepForm } from '../components/MultiStepForm';
import { WealthAccessSection } from '../components/WealthAccessSection';
import { ProcessSteps } from '../components/ProcessSteps';
import { FAQAccordion } from '../components/FAQAccordion';
import { CalculatorCTA } from '../components/CalculatorCTA';
import { GoogleReviews } from '../components/GoogleReviews';
export function HomePage() {
  return <main className="w-full" data-id="element-488">
      {/* Hero Section */}
      <section className="w-full bg-white py-16 md:py-24 px-4" data-id="element-489">
        <div className="max-w-7xl mx-auto" data-id="element-490">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-491">
            {/* Left Column: Content */}
            <div className="text-center lg:text-left" data-id="element-492">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-493">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 font-medium rounded-full mb-6 border border-accent/30 text-[13px] font-[Poppins,_sans-serif] text-gray-700 bg-white shadow-sm" data-id="element-494">
                  <Shield className="w-4 h-4 text-accent" data-id="element-495" />
                  Trusted by Canadian homeowners
                </span>
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-[1.1]" data-id="element-496">
                Unlock the <span className="text-accent" data-id="element-497">Power</span> of Your
                Home's Equity
              </motion.h1>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 mb-8 leading-relaxed font-light" data-id="element-498">
                Join thousands of Canadian homeowners who have accessed their
                equity to fund their dreams — fast approvals, flexible terms,
                and funding in as little as 48 hours
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8" data-id="element-499">
                <Button variant="primary" size="lg" icon href="#form" data-id="element-500">
                  Get Started
                </Button>
                <Button variant="outline" size="lg" href="#how-it-works" data-id="element-501">
                  How It Works
                </Button>
              </motion.div>

              {/* Benefit Checkmarks */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6 text-sm" data-id="element-502">
                <div className="flex items-center gap-2" data-id="element-503">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" data-id="element-504" />
                  <span className="text-gray-600 font-medium" data-id="element-505">
                    Same-day approvals
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-506">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" data-id="element-507" />
                  <span className="text-gray-600 font-medium" data-id="element-508">
                    Fast digital application
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-509">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" data-id="element-510" />
                  <span className="text-gray-600 font-medium" data-id="element-511">
                    Up to 80% LTV
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Right Column: Image with Floating Stat Cards */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative" data-id="element-512">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-513">
                <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80" alt="Beautiful modern home" className="w-full h-auto object-cover" data-id="element-514" />
              </div>

              {/* Floating Stat Card 1 - Top Right */}
              <motion.div initial={{
              opacity: 0,
              y: -30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.5
            }} className="hidden md:block absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4 w-56" data-id="element-515">
                <div className="flex items-center gap-3" data-id="element-516">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-517">
                    <Clock className="w-5 h-5 text-accent" data-id="element-518" />
                  </div>
                  <div data-id="element-519">
                    <p className="text-xs text-gray-500 font-medium" data-id="element-520">
                      Avg. Approval Time
                    </p>
                    <p className="text-2xl font-extrabold text-accent" data-id="element-521">
                      24 hrs
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Stat Card 2 - Bottom Left */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.6
            }} className="hidden md:block absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 w-56" data-id="element-522">
                <div className="flex items-center gap-3" data-id="element-523">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-524">
                    <DollarSign className="w-5 h-5 text-accent" data-id="element-525" />
                  </div>
                  <div data-id="element-526">
                    <p className="text-xs text-gray-500 font-medium" data-id="element-527">
                      Average Funding
                    </p>
                    <p className="text-2xl font-extrabold text-accent" data-id="element-528">
                      $185,000
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works - 3 Step Process */}
      <section id="how-it-works" className="w-full bg-white py-20 px-4" data-id="element-529">
        <div className="max-w-7xl mx-auto" data-id="element-530">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-16" data-id="element-531">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4" data-id="element-532">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-533">
              Get funded in three simple steps
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 relative" data-id="element-534">
            {/* Step 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="relative" data-id="element-535">
              <div className="bg-white rounded-xl shadow-sm p-8 text-center h-full border border-gray-100 hover:shadow-md transition-shadow" data-id="element-536">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary text-white rounded-full font-bold text-lg mb-6" data-id="element-537">
                  1
                </div>
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-6" data-id="element-538">
                  <ClipboardCheck className="w-8 h-8 text-primary" data-id="element-539" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-540">
                  Check Your Eligibility
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-541">
                  Answer a few quick questions about your property to see if you
                  qualify
                </p>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="relative" data-id="element-542">
              <div className="bg-white rounded-xl shadow-sm p-8 text-center h-full border border-gray-100 hover:shadow-md transition-shadow" data-id="element-543">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary text-white rounded-full font-bold text-lg mb-6" data-id="element-544">
                  2
                </div>
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-6" data-id="element-545">
                  <Calculator className="w-8 h-8 text-primary" data-id="element-546" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-547">
                  Get Your Funding Amount
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-548">
                  We'll assess your home's equity and present you with a
                  personalized offer
                </p>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="relative" data-id="element-549">
              <div className="bg-white rounded-xl shadow-sm p-8 text-center h-full border border-gray-100 hover:shadow-md transition-shadow" data-id="element-550">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary text-white rounded-full font-bold text-lg mb-6" data-id="element-551">
                  3
                </div>
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-6" data-id="element-552">
                  <Banknote className="w-8 h-8 text-primary" data-id="element-553" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-554">
                  Receive Your Funds
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-555">
                  Once approved, funds are deposited directly into your account
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="form" className="w-full bg-cream py-20 px-4" data-id="element-556">
        <div className="max-w-2xl mx-auto" data-id="element-557">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-558">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4" data-id="element-559">
              Check Your Eligibility
            </h2>
            <p className="text-xl text-gray-600" data-id="element-560">
              Get your free assessment in minutes — no credit check required
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} data-id="element-561">
            <MultiStepForm data-id="element-562" />
          </motion.div>
        </div>
      </section>

      {/* Wealth Access Section */}
      <WealthAccessSection data-id="element-563" />

      {/* Process Steps */}
      <ProcessSteps data-id="element-564" />

      {/* Calculator CTA Section */}
      <CalculatorCTA data-id="element-565" />

      {/* Google Reviews Section */}
      <GoogleReviews data-id="element-566" />

      {/* FAQ Section */}
      <FAQAccordion data-id="element-567" />
    </main>;
}